import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import Cookies from 'js-cookie';
import { fetchUserProfile } from '../api'; // Import named export

// Create the context
const AuthContext = createContext(null);

// Create a provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store user object (e.g., { id, email, username })
  const [token, setToken] = useState(() => Cookies.get('authToken') || null); // Initialize token from cookies
  const [isLoading, setIsLoading] = useState(true); // To track initial auth check

  // Function to set token in state and cookie
  const setAuthToken = (newToken) => {
    if (newToken) {
      Cookies.set('authToken', newToken, { expires: 7, path: '/', secure: true, sameSite: 'strict' }); // Add secure/samesite
      // --- Cannot set default header if apiClient is not imported ---
      // apiClient.defaults.headers.common['Authorization'] = `Bearer ${newToken}`; // Update axios default header
    } else {
      Cookies.remove('authToken', { path: '/' });
      // --- Cannot delete default header if apiClient is not imported ---
      // delete apiClient.defaults.headers.common['Authorization'];
    }
    setToken(newToken);
  };

  // Login function
  const login = useCallback((userData, newToken) => {
    setUser(userData); // Set the user object provided by login API
    setAuthToken(newToken);
  }, []);

  // Logout function
  const logout = useCallback(() => {
    setUser(null);
    setAuthToken(null);
    // Optionally clear other user-related cookies/localStorage here
    Cookies.remove('userId', { path: '/' });
    Cookies.remove('cartId', { path: '/' });
    // Optionally redirect the user after logout
    // window.location.href = '/login'; // Simple redirect, consider using useNavigate hook if called from a component
  }, []);

  // Check authentication status on initial load
  useEffect(() => {
    const checkAuth = async () => {
        const currentToken = Cookies.get('authToken');
        if (currentToken) {
            setToken(currentToken);
            // --- Cannot set default header if apiClient is not imported ---
            // apiClient.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
            try {
                // --- Use imported function ---
                const response = await fetchUserProfile(); // Use named import
                if (response.data) {
                    setUser(response.data); // Assuming the endpoint returns the user object
                } else {
                    // Token might be invalid or expired
                    logout();
                }
            } catch (error) {
                console.error("Auth check failed:", error);
                // Token likely invalid/expired
                logout();
            }
        }
        setIsLoading(false);
    };
    checkAuth();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Value provided to consuming components
  const value = {
    user,
    token,
    isLoggedIn: !!token, // Boolean flag derived from token presence
    isLoading, // Provide loading state for initial check
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Create a hook for easy consumption
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined || context === null) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}; 